import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, ChevronRight, Building2, Users, DollarSign, Clock, CheckCircle2, Sparkles, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { CreateProspectDialog } from './CreateProspectDialog';
import { FixedBidQuoteLines, MarkupPercentField, type MarkupInput } from './FixedBidMarkup';
import { firmDefaultMarkupPercent, fixedBidQuote } from '../utils/fixedBidMarkup';

interface CreateProposalDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  accessToken: string;
  currentUser: any;
}

interface EngagementTemplate {
  id: string;
  name: string;
  description: string;
  estimatedHours: string;
  priceRange: string;
  deploymentType?: string;
  tags: string[];
  deliverables: string[];
  timeline: {
    startDate?: string;
    endDate?: string;
    estimatedWeeks?: number;
  };
  pricingType: 'fixed' | 'hourly' | 'hybrid';
}

export function CreateProposalDialog({
  open,
  onClose,
  onSuccess,
  accessToken,
  currentUser,
}: CreateProposalDialogProps) {
  const [step, setStep] = useState<'client-selection' | 'template-selection' | 'details'>('client-selection');
  const [saving, setSaving] = useState(false);
  const [loadingProspects, setLoadingProspects] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [prospects, setProspects] = useState<any[]>([]);
  const [engagementTemplates, setEngagementTemplates] = useState<EngagementTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EngagementTemplate | null>(null);
  const [useTemplate, setUseTemplate] = useState<boolean>(true);
  const [createProspectOpen, setCreateProspectOpen] = useState(false);
  // A new fixed bid starts from the firm's default markup (Firm › Settings › Scoping).
  const [defaultMarkup, setDefaultMarkup] = useState(0);
  
  const [formData, setFormData] = useState({
    clientType: 'prospect' as 'prospect' | 'company',
    prospectId: '',
    companyId: '',
    name: '',
    description: '',
    pricingType: 'fixed' as 'fixed' | 'hourly' | 'hybrid',
    amount: 0,
    markupPercent: 0 as MarkupInput,
    startDate: '',
    endDate: '',
    engagementTemplateId: '',
  });
  const quote = fixedBidQuote(formData.amount, formData.markupPercent);

  const loadDefaultMarkup = async () => {
    const firmId = currentUser.firmId || currentUser.consultingFirmId;
    if (!firmId) return;
    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consulting-firms/${encodeURIComponent(firmId)}`, { headers: { 'Authorization': `Bearer ${accessToken}` } });
      if (!response.ok) return;
      const markup = firmDefaultMarkupPercent((await response.json())?.firm);
      setDefaultMarkup(markup);
      setFormData((prev) => ({ ...prev, markupPercent: markup }));
    } catch (error) {
      console.error('Error loading the firm default markup:', error);
    }
  };

  useEffect(() => {
    if (open) {
      loadProspects();
      loadEngagementTemplates();
      loadDefaultMarkup();
      // Reset to first step when dialog opens
      setStep('client-selection');
      setSelectedTemplate(null);
      setUseTemplate(true);
    }
  }, [open]);

  const loadProspects = async () => {
    setLoadingProspects(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/bd/prospects`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProspects(data.prospects || []);
      }
    } catch (error) {
      console.error('Error loading prospects:', error);
    } finally {
      setLoadingProspects(false);
    }
  };

  const loadEngagementTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const firmId = currentUser.firmId || currentUser.consultingFirmId;
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-types?firmId=${firmId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setEngagementTemplates(data.engagementTypes || []);
      }
    } catch (error) {
      console.error('Error loading engagement templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (template: EngagementTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      ...formData,
      name: template.name,
      description: template.description,
      pricingType: template.pricingType,
      engagementTemplateId: template.id,
    });
  };

  const handleNextFromClientSelection = () => {
    if (formData.clientType === 'prospect' && !formData.prospectId) {
      toast.error('Please select a prospect or create a new one');
      return;
    }
    setStep('template-selection');
  };

  const handleNextFromTemplateSelection = () => {
    setStep('details');
  };

  const handleBack = () => {
    if (step === 'details') {
      setStep('template-selection');
    } else if (step === 'template-selection') {
      setStep('client-selection');
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a proposal name');
      return;
    }

    if (!formData.prospectId && formData.clientType === 'prospect') {
      toast.error('Please select a prospect');
      return;
    }

    setSaving(true);
    try {
      // Create proposal linked to prospect (not company yet)
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/bd/proposals`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            ...formData,
            // A fixed bid sends its markup; the server stores the bid total as the amount.
            markupPercent: formData.pricingType === 'fixed' ? quote.percent : undefined,
            status: 'draft',
            createdBy: currentUser.id,
            engagementTemplateId: selectedTemplate?.id || null,
            deliverables: selectedTemplate?.deliverables || [],
            timeline: selectedTemplate?.timeline || {
              startDate: formData.startDate,
              endDate: formData.endDate,
            },
          }),
        }
      );

      if (response.ok) {
        toast.success('Proposal created');
        onSuccess();
        // Reset form
        setFormData({
          clientType: 'prospect',
          prospectId: '',
          companyId: '',
          name: '',
          description: '',
          pricingType: 'fixed',
          amount: 0,
          markupPercent: defaultMarkup,
          startDate: '',
          endDate: '',
          engagementTemplateId: '',
        });
        setSelectedTemplate(null);
        setStep('client-selection');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create proposal');
      }
    } catch (error) {
      console.error('Error creating proposal:', error);
      toast.error('Failed to create proposal');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateProspectSuccess = (prospectId: string) => {
    setCreateProspectOpen(false);
    loadProspects(); // Reload prospects list
    setFormData({ ...formData, prospectId }); // Auto-select the new prospect
    toast.success('Prospect created and selected');
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {step === 'client-selection' && 'Select Client'}
              {step === 'template-selection' && 'Choose Engagement Template (Optional)'}
              {step === 'details' && 'Proposal Details'}
            </DialogTitle>
            <DialogDescription>
              {step === 'client-selection' && 'Select a prospect for this proposal'}
              {step === 'template-selection' && 'Start with a pre-configured engagement type or create from scratch'}
              {step === 'details' && 'Review and customize your proposal'}
            </DialogDescription>
          </DialogHeader>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 py-2">
            <div className={`flex items-center gap-2 ${step === 'client-selection' ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`rounded-full size-8 flex items-center justify-center border-2 ${step === 'client-selection' ? 'border-primary bg-primary/10' : 'border-gray-300'}`}>
                1
              </div>
              <span className="text-sm font-medium">Client</span>
            </div>
            <ChevronRight className="size-4 text-muted-foreground" />
            <div className={`flex items-center gap-2 ${step === 'template-selection' ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`rounded-full size-8 flex items-center justify-center border-2 ${step === 'template-selection' ? 'border-primary bg-primary/10' : 'border-gray-300'}`}>
                2
              </div>
              <span className="text-sm font-medium">Template</span>
            </div>
            <ChevronRight className="size-4 text-muted-foreground" />
            <div className={`flex items-center gap-2 ${step === 'details' ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`rounded-full size-8 flex items-center justify-center border-2 ${step === 'details' ? 'border-primary bg-primary/10' : 'border-gray-300'}`}>
                3
              </div>
              <span className="text-sm font-medium">Details</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto py-4">
            {/* Step 1: Client Selection */}
            {step === 'client-selection' && (
              <div className="space-y-4">
                <div>
                  <Label>Select Prospect *</Label>
                  <div className="flex gap-2 mt-2">
                    <Select 
                      value={formData.prospectId} 
                      onValueChange={(value) => setFormData({ ...formData, prospectId: value })}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select a prospect" />
                      </SelectTrigger>
                      <SelectContent>
                        {loadingProspects ? (
                          <SelectItem value="loading" disabled>Loading prospects...</SelectItem>
                        ) : prospects.length === 0 ? (
                          <SelectItem value="none" disabled>No prospects available — create one first</SelectItem>
                        ) : (
                          prospects.map(prospect => (
                            <SelectItem key={prospect.id} value={prospect.id}>
                              {prospect.companyName} - {prospect.contactName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCreateProspectOpen(true)}
                      className="whitespace-nowrap"
                    >
                      <Plus className="size-4 mr-2" />
                      New Prospect
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Prospects will automatically convert to clients when you upload a signed SOW
                  </p>
                </div>

                {formData.prospectId && (
                  <Card className="border-primary/20 bg-primary/5">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Selected Prospect</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const prospect = prospects.find(p => p.id === formData.prospectId);
                        return prospect ? (
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <Building2 className="size-4 text-muted-foreground" />
                              <span className="font-medium">{prospect.companyName}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="size-4 text-muted-foreground" />
                              <span>{prospect.contactName} ({prospect.contactEmail})</span>
                            </div>
                            {prospect.industry && (
                              <div className="text-muted-foreground">
                                Industry: {prospect.industry}
                              </div>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Step 2: Template Selection */}
            {step === 'template-selection' && (
              <div className="space-y-4">
                <RadioGroup value={useTemplate ? 'template' : 'blank'} onValueChange={(value) => setUseTemplate(value === 'template')}>
                  <div className="flex items-center space-x-2 p-4 border rounded-lg cursor-pointer hover:bg-accent" onClick={() => setUseTemplate(false)}>
                    <RadioGroupItem value="blank" id="blank" />
                    <Label htmlFor="blank" className="flex-1 cursor-pointer">
                      <div className="font-medium">Generate Blank Proposal</div>
                      <div className="text-sm text-muted-foreground">Start from scratch with an empty proposal</div>
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2 p-4 border rounded-lg cursor-pointer hover:bg-accent" onClick={() => setUseTemplate(true)}>
                    <RadioGroupItem value="template" id="template" />
                    <Label htmlFor="template" className="flex-1 cursor-pointer">
                      <div className="font-medium flex items-center gap-2">
                        <Sparkles className="size-4 text-purple-500" />
                        Start with Engagement Template (Recommended)
                      </div>
                      <div className="text-sm text-muted-foreground">Select a pre-configured engagement type to auto-populate this proposal</div>
                    </Label>
                  </div>
                </RadioGroup>

                {useTemplate && (
                  <div className="space-y-3 mt-6">
                    <div className="flex items-center justify-between">
                      <Label>Available Engagement Templates</Label>
                      {loadingTemplates && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                    </div>
                    
                    {engagementTemplates.length === 0 && !loadingTemplates ? (
                      <Card className="border-dashed">
                        <CardContent className="pt-6 text-center text-muted-foreground">
                          <p>No engagement templates found for this engagement type.</p>
                          <p className="text-sm mt-2">You can create custom engagement templates in Settings.</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                        {engagementTemplates.map((template) => (
                          <Card 
                            key={template.id} 
                            className={`cursor-pointer transition-all ${selectedTemplate?.id === template.id ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/50'}`}
                            onClick={() => handleTemplateSelect(template)}
                          >
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <CardTitle className="text-base">{template.name}</CardTitle>
                                  <CardDescription className="text-sm mt-1">{template.description}</CardDescription>
                                </div>
                                {selectedTemplate?.id === template.id && (
                                  <CheckCircle2 className="size-5 text-primary flex-shrink-0 ml-2" />
                                )}
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="flex flex-wrap items-center gap-4 text-sm">
                                <div className="flex items-center gap-1.5 text-muted-foreground">
                                  <Clock className="size-4" />
                                  <span>{template.estimatedHours}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-muted-foreground">
                                  <DollarSign className="size-4" />
                                  <span>{template.priceRange}</span>
                                </div>
                                {template.deploymentType && (
                                  <Badge variant="outline">{template.deploymentType}</Badge>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-3">
                                {template.tags.map((tag) => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Proposal Details */}
            {step === 'details' && (
              <div className="space-y-4">
                {selectedTemplate && (
                  <Card className="border-primary/20 bg-primary/5">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">Based on Template: {selectedTemplate.name}</CardTitle>
                        <Badge variant="secondary">
                          <Sparkles className="size-3 mr-1" />
                          Template
                        </Badge>
                      </div>
                    </CardHeader>
                  </Card>
                )}

                <div>
                  <Label htmlFor="name">Proposal Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Q1 2025 Implementation Proposal"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the proposal..."
                    rows={4}
                  />
                </div>

                <div>
                  <Label htmlFor="pricingType">Pricing Model</Label>
                  <Select 
                    value={formData.pricingType} 
                    onValueChange={(value: any) => setFormData({ ...formData, pricingType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed Price</SelectItem>
                      <SelectItem value="hourly">Hourly Rate</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className={formData.pricingType === 'fixed' ? 'grid grid-cols-2 gap-4' : ''}>
                  <div>
                    <Label htmlFor="amount">{formData.pricingType === 'fixed' ? 'Fixed Price ($)' : 'Estimated Amount ($)'}</Label>
                    <Input
                      id="amount"
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                      placeholder="75000"
                    />
                  </div>
                  {formData.pricingType === 'fixed' && (
                    <MarkupPercentField id="new-proposal-markup" value={formData.markupPercent} onChange={(v) => setFormData({ ...formData, markupPercent: v })} />
                  )}
                </div>
                {formData.pricingType === 'fixed' && quote.percent > 0 && (
                  <FixedBidQuoteLines quote={quote} testId="new-proposal-fixed-bid-quote" className="text-sm rounded-md border p-3" />
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startDate">Expected Start Date</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="endDate">Expected End Date</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <div>
                {step !== 'client-selection' && (
                  <Button variant="ghost" onClick={handleBack}>
                    Back
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                {step === 'client-selection' && (
                  <Button onClick={handleNextFromClientSelection} disabled={!formData.prospectId}>
                    Next
                    <ChevronRight className="size-4 ml-2" />
                  </Button>
                )}
                {step === 'template-selection' && (
                  <Button onClick={handleNextFromTemplateSelection}>
                    Next
                    <ChevronRight className="size-4 ml-2" />
                  </Button>
                )}
                {step === 'details' && (
                  <Button onClick={handleSave} disabled={saving}>
                    {saving && <Loader2 className="size-4 mr-2 animate-spin" />}
                    Create Proposal
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Prospect Dialog */}
      <CreateProspectDialog
        open={createProspectOpen}
        onClose={() => setCreateProspectOpen(false)}
        onSuccess={handleCreateProspectSuccess}
        accessToken={accessToken}
      />
    </>
  );
}